const TONED: Record<string, string> = {
  a: "āáǎà",
  e: "ēéěè",
  i: "īíǐì",
  o: "ōóǒò",
  u: "ūúǔù",
  ü: "ǖǘǚǜ",
};

const BASE = new Map<string, [string, number]>();
for (const [vowel, marks] of Object.entries(TONED)) {
  for (let i = 0; i < marks.length; i++) {
    BASE.set(marks[i]!, [vowel, i + 1]);
    BASE.set(marks[i]!.toUpperCase(), [vowel.toUpperCase(), i + 1]);
  }
}

function markVowel(syllable: string, tone: number): string {
  const s = syllable.replace(/u:/g, "ü").replace(/U:/g, "Ü").replace(/v/g, "ü").replace(/V/g, "Ü");
  if (tone < 1 || tone > 4) return s;
  const lower = s.toLowerCase();
  let at = lower.indexOf("a");
  if (at < 0) at = lower.indexOf("e");
  if (at < 0) at = lower.indexOf("ou");
  if (at < 0) {
    for (let i = lower.length - 1; i >= 0; i--) {
      if ("iouü".includes(lower[i]!)) {
        at = i;
        break;
      }
    }
  }
  if (at < 0) return s;
  const marks = TONED[lower[at]!];
  if (!marks) return s;
  const marked = s[at] === lower[at] ? marks[tone - 1]! : marks[tone - 1]!.toUpperCase();
  return s.slice(0, at) + marked + s.slice(at + 1);
}

// "ni3hao3" -> "nǐhǎo"; unnumbered letters stay unchanged while typing.
export function toneMarks(text: string): string {
  return text.replace(/([a-zü:]+?)([0-5])/gi, (_, letters: string, digit: string) =>
    markVowel(letters, Number(digit)),
  );
}

export type Tone = 1 | 2 | 3 | 4 | 5;
export const TONES: readonly [Tone, ...Tone[]] = [1, 2, 3, 4, 5];

// ("hao", 3) -> "hǎo"; the neutral tone 5 leaves the letters bare.
export function markSyllable(letters: string, tone: Tone): string {
  return markVowel(letters, tone);
}

// True once the text carries any tone, as a digit or a marked vowel.
export function hasTones(text: string): boolean {
  return /\d/.test(text) || [...text.normalize("NFC")].some((ch) => BASE.has(ch));
}

// "nǐhǎo" -> "ni3ha3o": digits follow marked vowels, not syllable boundaries.
export function toneNumbers(text: string): string {
  let out = "";
  for (const ch of text) {
    const hit = BASE.get(ch);
    out += hit ? hit[0] + hit[1] : ch;
  }
  return out;
}

// Compare marked, numbered or mixed pinyin; numbered neutral tones disappear.
// TODO: Normalize Unicode to NFC before conversion so decomposed tone marks compare and split correctly.
export function canonical(text: string): string {
  return toneNumbers(toneMarks(text.toLowerCase()))
    .replace(/u:/g, "ü")
    .replace(/v/g, "ü")
    .replace(/[\s'’\-]/g, "");
}

export function samePinyin(a: string, b: string): boolean {
  return canonical(a) === canonical(b);
}

const SYLLABLES = new Set(
  ("a ai an ang ao ba bai ban bang bao bei ben beng bi bian biang biao bie bin bing bo bu ca cai can cang cao ce cen " +
    "ceng cha chai chan chang chao che chen cheng chi chong chou chu chua chuai chuan chuang chui chun chuo ci cong cou " +
    "cu cuan cui cun cuo da dai dan dang dao de dei den deng di dia dian diao die ding diu dong dou du duan dui dun duo " +
    "e ei en eng er fa fan fang fei fen feng fiao fo fou fu ga gai gan gang gao ge gei gen geng gong gou gu gua guai " +
    "guan guang gui gun guo ha hai han hang hao he hei hen heng hong hou hu hua huai huan huang hui hun huo ji jia jian " +
    "jiang jiao jie jin jing jiong jiu ju juan jue jun ka kai kan kang kao ke kei ken keng kong kou ku kua kuai kuan " +
    "kuang kui kun kuo la lai lan lang lao le lei leng li lia lian liang liao lie lin ling liu lo long lou lu luan lun " +
    "luo lü lüe ma mai man mang mao me mei men meng mi mian miao mie min ming miu mo mou mu na nai nan nang nao ne nei " +
    "nen neng ni nian niang niao nie nin ning niu nong nou nu nuan nuo nü nüe o ou pa pai pan pang pao pei pen peng pi " +
    "pian piao pie pin ping po pou pu qi qia qian qiang qiao qie qin qing qiong qiu qu quan que qun ran rang rao re ren " +
    "reng ri rong rou ru rua ruan rui run ruo sa sai san sang sao se sei sen seng sha shai shan shang shao she shei " +
    "shen sheng shi shou shu shua shuai shuan shuang shui shun shuo si song sou su suan sui sun suo ta tai tan tang tao " +
    "te tei teng ti tian tiao tie ting tong tou tu tuan tui tun tuo wa wai wan wang wei wen weng wo wu xi xia xian " +
    "xiang xiao xie xin xing xiong xiu xu xuan xue xun ya yan yang yao ye yi yin ying yo yong you yu yuan yue yun za zai " +
    "zan zang zao ze zei zen zeng zha zhai zhan zhang zhao zhe zhei zhen zheng zhi zhong zhou zhu zhua zhuai zhuan " +
    "zhuang zhui zhun zhuo zi zong zou zu zuan zui zun zuo").split(" "),
);

export interface Syllable {
  base: string;
  tone: number;
  erhua: boolean;
}

export function syllables(text: string, chars?: number): Syllable[] | null {
  // TODO: Reject empty input and require a positive safe-integer chars count; success currently permits [].
  return split(text, chars, true) ?? split(text, chars, false);
}

function split(text: string, chars: number | undefined, strict: boolean): Syllable[] | null {
  // TODO: Parse letters, tones and boundaries once for both passes; avoid spreading breaks for each candidate.
  const letters: string[] = [];
  const tones: number[] = [];
  const breaks = new Set<number>([0]);
  for (const ch of toneMarks(text.toLowerCase()).replace(/u:/g, "ü").replace(/v/g, "ü")) {
    const hit = BASE.get(ch);
    if (hit) {
      letters.push(hit[0]);
      tones.push(hit[1]);
    } else if (/[a-zü]/.test(ch)) {
      letters.push(ch);
      tones.push(0);
    } else if (/[\s'’\-·]/.test(ch)) {
      breaks.add(letters.length);
    } else {
      return null;
    }
  }
  const n = letters.length;
  // TODO: Keep only the best split per offset/remaining character weight; enumerating every split can exhaust memory.
  const memo = new Map<number, Syllable[][]>();
  const from = (i: number): Syllable[][] => {
    if (i === n) return [[]];
    const cached = memo.get(i);
    if (cached) return cached;
    const out: Syllable[][] = [];
    if (!strict || breaks.has(i) || !"aeo".includes(letters[i]!)) {
      for (let len = Math.min(6, n - i); len >= 1; len--) {
        const base = letters.slice(i, i + len).join("");
        if (!SYLLABLES.has(base)) continue;
        if ([...breaks].some((b) => b > i && b < i + len)) continue;
        const marked = tones.slice(i, i + len).filter((t) => t > 0);
        if (marked.length > 1) continue;
        const tone = marked[0] ?? 5;
        let end = i + len;
        const options: [number, boolean][] = [[end, false]];
        if (letters[end] === "r" && base !== "er" && !breaks.has(end) && (end + 1 === n || breaks.has(end + 1) || !"aeiouü".includes(letters[end + 1]!))) {
          options.unshift([end + 1, true]);
        }
        for (const [next, erhua] of options) {
          for (const rest of from(next)) out.push([{ base: erhua ? base + "r" : base, tone, erhua }, ...rest]);
        }
      }
    }
    memo.set(i, out);
    return out;
  };
  const weight = (s: Syllable[]) => s.reduce((sum, x) => sum + (x.erhua ? 2 : 1), 0);
  const splits = from(0).filter((s) => chars === undefined || weight(s) === chars);
  if (!splits.length) return null;
  return splits.reduce((best, s) => (s.length < best.length ? s : best));
}

export function toneNumbersBySyllable(text: string, chars?: number): string | null {
  const split = syllables(text, chars);
  return split ? split.map((s) => s.base + s.tone).join(" ") : null;
}
